import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Button,
  Card,
  CarIcon,
  Chip,
  EcoFigure,
  EcoHostingIcon,
  EcoPanel,
  Heading,
  StatTile,
  VerifiedBadge,
} from '@openroad/ui';
import { api, unwrap } from '../lib/api';
import { isRealisedBooking, treesEquivalent, tripsShared } from '../lib/impact';
import { useEndSessionOnAuthFailure, useSession } from '../lib/session';

const BADGE_LABEL: Record<string, string> = {
  ID: 'ID verified',
  PHONE: 'Phone verified',
  EMAIL: 'Email verified',
  PHOTO: 'Photo verified',
};

const CHATTINESS_LABEL: Record<string, string> = {
  QUIET: 'Quiet ride',
  BALANCED: 'Chat if you feel like it',
  CHATTY: 'Happy to chat',
};

const PETS_LABEL: Record<string, string> = {
  WELCOME: 'Pets welcome',
  BY_ARRANGEMENT: 'Pets by arrangement',
  NONE: 'No pets',
};

export function ProfileScreen() {
  const navigate = useNavigate();
  const { member, signOut } = useSession();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => unwrap(api.GET('/auth/me')),
  });

  const profile = useQuery({
    queryKey: ['user', member?.id],
    queryFn: () => unwrap(api.GET('/users/{id}', { params: { path: { id: member!.id } } })),
    enabled: Boolean(member?.id),
  });

  const bookings = useQuery({
    queryKey: ['bookings', 'mine'],
    queryFn: () => unwrap(api.GET('/bookings/mine', { params: { query: { when: 'all' } } })),
  });

  const driving = useQuery({
    queryKey: ['trips', 'mine'],
    queryFn: () => unwrap(api.GET('/trips/mine', { params: { query: { when: 'all' } } })),
  });

  // A 401 here means the refresh already failed inside the client, so there is
  // no session left to keep. Ending it sends the member to sign in rather than
  // leaving them on a screen that cannot load.
  const expired = useEndSessionOnAuthFailure(
    me.error,
    profile.error,
    bookings.error,
    driving.error,
  );

  if (me.isPending) {
    return (
      <main className="mx-auto max-w-[920px] px-6 py-14">
        <p className="text-body text-muted">Loading your profile…</p>
      </main>
    );
  }

  if (me.isError || !me.data) {
    return (
      <main className="mx-auto max-w-[560px] px-6 py-14">
        <Card radius="panel" className="p-7 text-center">
          <Heading level="panel" className="mb-2">
            {expired ? 'Please sign in again' : 'We could not load your profile'}
          </Heading>
          <p className="m-0 mb-6 text-base text-muted">
            {expired
              ? 'Your session has ended. Signing in again will bring everything back.'
              : 'Something went wrong on our side. Try again in a moment.'}
          </p>
          {expired ? null : (
            <Button variant="cta" onClick={() => void me.refetch()}>
              Try again
            </Button>
          )}
        </Card>
      </main>
    );
  }

  const self = me.data;
  const ridden = (bookings.data?.items ?? []).filter(isRealisedBooking);
  const savedGrams = ridden.reduce((total, booking) => total + booking.co2SavedGrams, 0);
  const savedKilograms = Math.round(savedGrams / 1000);
  const trees = treesEquivalent(savedGrams);
  const shared = tripsShared({
    driving: driving.data?.items ?? [],
    bookings: bookings.data?.items ?? [],
  });

  /**
   * Both figures below are totals over two requests. If either has not arrived
   * — or failed — the sum is not a total, it is whatever happened to load, so
   * it shows a dash instead of a number somebody could read as a fact.
   */
  const figuresReady = bookings.isSuccess && driving.isSuccess;
  const figuresFailed = bookings.isError || driving.isError;

  const preferences = [
    CHATTINESS_LABEL[self.chattiness] ?? self.chattiness,
    self.musicWelcome ? 'Music welcome' : 'Prefers no music',
    PETS_LABEL[self.petsPolicy] ?? self.petsPolicy,
    self.smokingAllowed ? 'Smoking allowed' : 'No smoking',
  ];

  const vehicles = profile.data?.vehicles ?? [];

  return (
    <main className="mx-auto max-w-[920px] px-6 pt-7 pb-16">
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-[18px]">
          {/* identity */}
          <Card radius="panel" className="p-6">
            <div className="flex items-start gap-[18px]">
              <Avatar
                initials={self.initials}
                colour={self.avatarColour}
                size={72}
                name={self.displayName}
              />
              <div className="flex-1">
                <Heading level="panel" as="h1">
                  {self.displayName}
                </Heading>
                <div className="mt-[3px] text-label text-muted">
                  <span className="font-bold text-ngahere">★ {self.ratingAverage ?? '—'}</span> ·{' '}
                  {self.reviewCount} {self.reviewCount === 1 ? 'review' : 'reviews'} · Member since{' '}
                  {new Date(self.memberSince).getFullYear()}
                </div>
                {self.bio ? (
                  <p className="mt-3 mb-0 text-base leading-[1.55] text-ink-2">{self.bio}</p>
                ) : null}
              </div>
              {/*
                The export has no sign-out control — it is a visual prototype
                with no session to end. A signed-in member needs one.
              */}
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  await signOut();
                  navigate('/');
                }}
              >
                Sign out
              </Button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {self.verifiedBadges.map((badge) => (
                <VerifiedBadge key={badge}>{BADGE_LABEL[badge] ?? badge}</VerifiedBadge>
              ))}
            </div>
          </Card>

          {/* vehicle and ride preferences */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card radius="panel" className="p-5">
              <span className="text-caption font-bold tracking-eyebrow text-subtle uppercase">
                Vehicle
              </span>
              {profile.isError ? (
                <p className="mt-3 mb-0 text-label text-muted">
                  We could not load your vehicle just now.
                </p>
              ) : vehicles.length === 0 ? (
                <p className="mt-3 mb-0 text-label text-muted">
                  {profile.isPending ? '—' : 'No vehicle on your account yet.'}
                </p>
              ) : (
                vehicles.map((vehicle) => (
                  <div key={vehicle.id} className="mt-3 flex items-center gap-[11px]">
                    <CarIcon size={22} strokeWidth={1.6} className="text-ink-2" />
                    <span className="text-body-lg font-semibold">
                      {vehicle.make} {vehicle.model}
                    </span>
                  </div>
                ))
              )}
            </Card>

            <Card radius="panel" className="p-5">
              <span className="text-caption font-bold tracking-eyebrow text-subtle uppercase">
                Ride preferences
              </span>
              <div className="mt-3 flex flex-wrap gap-[7px]">
                {preferences.map((preference) => (
                  <Chip key={preference}>{preference}</Chip>
                ))}
              </div>
            </Card>
          </div>
        </div>

        {/* ------------------------------------------------------- sidebar */}
        <div className="flex flex-col gap-3.5 lg:sticky lg:top-[84px]">
          <EcoPanel>
            {/*
              The export labels this "Lifetime CO₂ saved". It counts only seats
              this member has taken, because that is the only realised saving
              the API attributes to a person — a driver's own share of a shared
              journey has no route to read it from. Labelling a partial figure
              as a lifetime total would be a wrong number, so the label says
              what the number is. See docs/design-fidelity.md.
            */}
            <EcoFigure
              eyebrow="CO₂ saved as a passenger"
              value={figuresReady ? savedKilograms.toLocaleString('en-NZ') : '—'}
              unit="kg"
              size="md"
              caption={
                figuresFailed
                  ? 'We could not load your figures just now.'
                  : figuresReady
                    ? `≈ ${trees.toLocaleString('en-NZ')} ${trees === 1 ? 'tree' : 'trees'} growing for a year`
                    : 'Adding these up…'
              }
            />
          </EcoPanel>

          <div className="grid grid-cols-2 gap-3.5">
            <StatTile value={figuresReady ? shared : '—'} label="trips shared" />
            <StatTile value={`${self.responseRatePct}%`} label="response rate" />
          </div>

          <Card className="flex items-center gap-2.5 p-4">
            <EcoHostingIcon size={18} strokeWidth={1.6} className="text-eco" />
            <span className="text-caption text-muted">
              Account hosted on <strong className="text-eco">somsorium EcoTechFarms</strong>
            </span>
          </Card>
        </div>
      </div>
    </main>
  );
}
